import TableCell from "@mui/material/TableCell";
import TableFooter from "@mui/material/TableFooter";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { BranchDaySummaryTotals } from "../../types/sale-deposit.type";
import { formatNumber, numberWithCommas } from "../../utils";
import DiffAmountText from "./DiffAmountText";
import NetOfNotesHint from "./NetOfNotesHint";
import TransferAmountText from "./TransferAmountText";
import {
  DATE_COLUMN_WIDTH,
  STICKY_BODY_Z_INDEX,
  stickyColumnSx,
} from "./tableLayout";
import {
  TOTALS_ROW_SX,
  TotalsCountCell,
  TotalsMoneyCell,
  TotalsSkeletonFooter,
  totalsCellSx,
} from "./totalsFooter";
import {
  formatCashInHand,
  pendingSlipHint,
  physicalCash,
  transferDiff,
} from "./transfer.utils";

interface BranchSummaryFooterProps {
  totals: BranchDaySummaryTotals | null;
  loading: boolean;
  /** Columns in the table, so the loading row spans the full width. */
  columnCount: number;
}

/** ตรวจแล้ว (BO), ตรวจแล้ว (HO) and สถานะการปิดยอด are per-branch-day states, not quantities
 * anything can be summed into, so the totals row leaves those three columns blank. */
const TRAILING_EMPTY_COLUMNS = 3;

/**
 * แถวสรุปยอด for the branch-day summary — the same row the route-level table closes with, one
 * altitude up.
 *
 * Every figure is the server's, aggregated over EVERY matching branch-day rather than the page
 * on screen, and `branch_day_count` is how many of them the row speaks for. ผลต่าง is derived
 * from the two totals rather than summed per row, with `pending_count` as a tooltip so a
 * branch-day that has not remitted yet does not read as a shortfall.
 */
export default function BranchSummaryFooter({
  totals,
  loading,
  columnCount,
}: BranchSummaryFooterProps) {
  if (loading) return <TotalsSkeletonFooter columnCount={columnCount} />;

  if (!totals || totals.branch_day_count === 0) return null;

  // Both operands are always present on the totals object, so the un-submitted case
  // transferDiff guards against cannot arise here.
  const diff =
    transferDiff(totals.required_transfer_amount, totals.transfer_amount) ?? 0;

  return (
    <TableFooter>
      <TableRow sx={TOTALS_ROW_SX}>
        <TableCell
          sx={{
            ...stickyColumnSx(0, STICKY_BODY_Z_INDEX),
            minWidth: DATE_COLUMN_WIDTH,
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            รวมทั้งหมด
          </Typography>
          <Typography component="span" fontWeight={700}>
            {numberWithCommas(totals.branch_day_count)} รายการ
          </Typography>
        </TableCell>
        <TableCell sx={stickyColumnSx(DATE_COLUMN_WIDTH, STICKY_BODY_Z_INDEX)} />
        <TotalsCountCell>{numberWithCommas(totals.route_count)}</TotalsCountCell>
        <TotalsCountCell>
          {numberWithCommas(totals.customer_count)}
        </TotalsCountCell>
        <TotalsMoneyCell>{formatNumber(totals.total_amount)}</TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.credit_total_value)}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatCashInHand(physicalCash(totals))}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.transfer_total_value)}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.cash_total_value)}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.deposit_collection_amount)}
        </TotalsMoneyCell>
        <TotalsMoneyCell>
          {formatNumber(totals.total_expense_amount)}
        </TotalsMoneyCell>
        <TableCell align="right" sx={totalsCellSx}>
          <Tooltip title={pendingSlipHint(totals.pending_count)}>
            <span>
              <TransferAmountText
                required={totals.required_transfer_amount}
                transfer={totals.transfer_amount}
              />
            </span>
          </Tooltip>
          <NetOfNotesHint totalAmount={totals.credit_debit_note_amount} />
        </TableCell>
        <TableCell align="right" sx={totalsCellSx}>
          <DiffAmountText diff={diff} />
        </TableCell>
        {Array.from({ length: TRAILING_EMPTY_COLUMNS }).map((_, index) => (
          <TableCell key={index} />
        ))}
      </TableRow>
    </TableFooter>
  );
}
